import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { createFurMaterial, addFur, updateFurMotion, setFurLight } from './fur.js';

/*
 * One Kli = the furry creature from creature/cute_creature.py, cloned as many times as
 * the page needs. Geometry is shared between clones; each clone gets its own fur
 * material (the fur reacts to that clone's motion).
 *
 * Space: the body is an ellipsoid 2 x 1.76 x 2 centred on the origin, facing +z.
 * The floor is at y = FLOOR. Head accessories go on `kli.head` (turns with the body),
 * props that float beside it go on `kli.stage` (doesn't turn).
 */

export const FLOOR = -0.98;
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

let template = null;
export function loadKliTemplate() {
  return new Promise((resolve, reject) => {
    new GLTFLoader().load('/landing/cute_creature.glb', (gltf) => {
      gltf.scene.traverse((o) => {
        if (!o.isMesh) return;
        if (o.material.name === 'EyeGloss') { o.material.color.set('#0b1217'); o.material.envMapIntensity = 0.5; }
        if (o.material.name === 'EyeGlint') o.material = new THREE.MeshBasicMaterial({ color: '#ffffff', toneMapped: false });
      });
      template = gltf.scene;
      resolve(template);
    }, undefined, reject);
  });
}

const dotMaterial = new THREE.MeshBasicMaterial({ color: '#2DACDF', toneMapped: false });   // --k-500

export function makeKli({ shells = 32, bubble = false, phase = 0 } = {}) {
  const scene = template.clone(true);
  const body = scene.getObjectByName('Body');

  // Rig at the feet so squash & hop happen against the floor
  const root = new THREE.Group();
  const rig = new THREE.Group();
  rig.position.y = FLOOR;
  root.add(rig);
  scene.position.y = -FLOOR;
  rig.add(scene);

  const stage = new THREE.Group();   // props that don't turn with the head
  root.add(stage);

  const blinkPivots = ['L', 'R'].map((side) => {
    const eye = body.getObjectByName(`Eye_${side}`);
    const glint = body.getObjectByName(`Glint_${side}`);
    const pivot = new THREE.Group();
    pivot.position.copy(eye.position);
    body.add(pivot);
    for (const o of [eye, glint]) { o.position.sub(pivot.position); pivot.add(o); }
    return pivot;
  });

  // Typing bubble: kept only where asked for
  let bubbleGroup = null;
  const dots = [];
  const parts = ['Bubble', 'Dot_1', 'Dot_2', 'Dot_3'].map((n) => body.getObjectByName(n));
  if (bubble) {
    bubbleGroup = new THREE.Group();
    bubbleGroup.position.copy(parts[0].position);
    bubbleGroup.userData.y = bubbleGroup.position.y;
    body.add(bubbleGroup);
    parts.forEach((o, i) => {
      o.position.sub(bubbleGroup.position);
      bubbleGroup.add(o);
      if (i) { o.userData.y = o.position.y; o.material = dotMaterial; dots.push(o); }
    });
  } else {
    parts.forEach((o) => o.removeFromParent());
  }

  const material = createFurMaterial({ shells, wind: !reducedMotion });
  addFur(body, material, shells);

  const kli = {
    root, rig, body, stage, head: body, material, bubble: bubbleGroup,
    squash: 0, squashVel: 0, hop: 0, hopVel: 0,
    nextBlink: 1 + Math.random() * 3, blinkStart: -10, phase,
    prevRotX: 0, prevRotY: 0, prevY: 0,
    look: new THREE.Vector2(),   // -1..1, where the cursor is relative to this Kli
    accessories: [],

    /** Squash into the floor and spring back. */
    poke(strength = 7) { this.squashVel -= strength; this.accessories.forEach((a) => a.poke?.()); },
    /** A little jump. */
    jump(v = 4.5) { if (this.hop < 0.02) this.hopVel = v; },

    add(accessory) { this.accessories.push(accessory); return accessory; },

    update(dt, t, camera, light) {
      const ease = 1 - Math.exp(-dt * 5);
      body.rotation.y += (this.look.x * 0.6 - body.rotation.y) * ease;
      body.rotation.x += (this.look.y * 0.28 - body.rotation.x) * ease;

      const breath = reducedMotion ? 0 : Math.sin(t * 2.1 + phase);
      const bob = reducedMotion ? 0 : Math.abs(Math.sin(t * 1.4 + phase)) * 0.04;

      this.squashVel += (-this.squash * 120 - this.squashVel * 9) * dt;
      this.squash += this.squashVel * dt;
      this.hopVel -= 16 * dt;
      this.hop = Math.max(0, this.hop + this.hopVel * dt);
      if (this.hop === 0 && this.hopVel < -2) { this.squashVel -= this.hopVel * 0.6; this.hopVel = 0; }   // land with a squish
      if (this.hop === 0) this.hopVel = Math.max(this.hopVel, 0);

      const sq = this.squash + breath * 0.012;
      rig.scale.set(1 - sq * 0.5, 1 + sq, 1 - sq * 0.5);
      rig.position.y = FLOOR + bob + this.hop - Math.max(0, -sq) * 0.9;

      if (!reducedMotion && t > this.nextBlink) { this.blinkStart = t; this.nextBlink = t + 2.5 + Math.random() * 3.5; }
      const ph = (t - this.blinkStart) / 0.16;
      const open = ph < 1 ? Math.max(Math.abs(ph * 2 - 1), 0.08) : 1;
      for (const p of blinkPivots) p.scale.y = open;

      if (bubbleGroup && !reducedMotion) {
        dots.forEach((d, i) => { d.position.y = d.userData.y + Math.max(0, Math.sin(t * 6 - i * 0.9)) * 0.06; });
        bubbleGroup.position.y = bubbleGroup.userData.y + Math.sin(t * 1.8 + 1) * 0.03;
        bubbleGroup.rotation.z = Math.sin(t * 1.2) * 0.04;
      }

      for (const a of this.accessories) a.update?.(dt, reducedMotion ? 0 : t, this);

      updateFurMotion(material, { dt, t, rotX: body.rotation.x, rotY: body.rotation.y,
        prevRotX: this.prevRotX, prevRotY: this.prevRotY, y: rig.position.y, prevY: this.prevY });
      this.prevRotX = body.rotation.x; this.prevRotY = body.rotation.y; this.prevY = rig.position.y;
      setFurLight(material, light.position, camera);
    },
  };
  return kli;
}

/* Soft contact shadow, shared texture */
let shadowTex = null;
export function contactShadow(w = 2.6, d = 2.2) {
  if (!shadowTex) {
    const c = document.createElement('canvas'); c.width = c.height = 128;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    grd.addColorStop(0, 'rgba(22,36,46,0.30)'); grd.addColorStop(1, 'rgba(22,36,46,0)');   // --ink
    g.fillStyle = grd; g.fillRect(0, 0, 128, 128);
    shadowTex = new THREE.CanvasTexture(c);
  }
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, d),
    new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false }));
  m.rotation.x = -Math.PI / 2;
  m.position.y = FLOOR + 0.004;
  return m;
}
